import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, ImagePlus, Pencil, Trash2, Undo2, X } from "lucide-react";
import { theme, btnPrimary, btnSecondary, floatingSheet } from "../theme";
import { api, apiBlob, apiErrorMessage } from "../api/client";

import { cbSafe } from "../lib/colourBlind";
// Screenshots on a bug report (routes/bugReports.routes.ts). A reporter can capture the
// screen, choose an image or paste one, then black out anything they would rather not send
// before it leaves the browser. Every image is redrawn on a canvas before upload, which
// drops its metadata (camera, location) and caps the size; the server still checks the bytes.

export interface PendingShot {
  key: string;
  blob: Blob;
  url: string;
  name: string;
  width: number;
  height: number;
}

export interface BugAttachment {
  id: string;
  messageId: string | null;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  width: number | null;
  height: number | null;
  fromAdmin: boolean;
  createdAt: string;
}

const MAX_DIMENSION = 2560;
const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPTED = ["image/png", "image/jpeg", "image/webp"];

let keySeq = 0;

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

/** Draws the source onto a fresh canvas (at most 2560px on the long side) and encodes it again. */
async function normalise(source: CanvasImageSource, width: number, height: number, name: string): Promise<PendingShot> {
  const scale = Math.min(1, MAX_DIMENSION / Math.max(width, height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("This browser cannot prepare images");
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  let blob = await canvasToBlob(canvas, "image/png");
  let ext = "png";
  // A photo-like screenshot can be large as PNG; JPEG keeps it under the limit.
  if (!blob || blob.size > MAX_BYTES) { blob = await canvasToBlob(canvas, "image/jpeg", 0.85); ext = "jpg"; }
  if (!blob || blob.size > MAX_BYTES) throw new Error("That image is too large to attach");
  const base = name.replace(/\.[a-z0-9]{1,5}$/i, "") || "screenshot";
  return { key: `shot-${++keySeq}`, blob, url: URL.createObjectURL(blob), name: `${base}.${ext}`, width: canvas.width, height: canvas.height };
}

async function fromFile(file: Blob, name: string): Promise<PendingShot> {
  if (!ACCEPTED.includes(file.type)) throw new Error("Only PNG, JPEG or WebP images can be attached");
  const bitmap = await createImageBitmap(file);
  try {
    return await normalise(bitmap, bitmap.width, bitmap.height, name);
  } finally {
    bitmap.close();
  }
}

type DisplayMediaOptions = DisplayMediaStreamOptions & { preferCurrentTab?: boolean; selfBrowserSurface?: string };

/** One frame of the screen, tab or window the person chooses. `hide` takes the dialog out of the way first. */
async function captureScreen(hide: (hidden: boolean) => void): Promise<PendingShot | null> {
  const opts: DisplayMediaOptions = { video: true, audio: false, preferCurrentTab: true, selfBrowserSurface: "include" };
  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getDisplayMedia(opts);
  } catch {
    return null; // cancelled or refused
  }
  hide(true);
  try {
    const video = document.createElement("video");
    video.muted = true;
    video.srcObject = stream;
    await video.play();
    // Let the page repaint without the dialog before taking the frame.
    await new Promise((r) => setTimeout(r, 450));
    return await normalise(video, video.videoWidth, video.videoHeight, `screenshot-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}`);
  } finally {
    stream.getTracks().forEach((t) => t.stop());
    hide(false);
  }
}

export const canCaptureScreen = typeof navigator !== "undefined" && !!navigator.mediaDevices && typeof navigator.mediaDevices.getDisplayMedia === "function";

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ""));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}

/** Uploads each shot in turn; returns the messages of any that failed. */
export async function uploadShots(path: string, shots: PendingShot[], messageId?: string | null): Promise<string[]> {
  const failures: string[] = [];
  for (const s of shots) {
    try {
      await api.post(path, { data: await blobToBase64(s.blob), fileName: s.name, messageId: messageId ?? null });
    } catch (e) {
      failures.push(`${s.name}: ${apiErrorMessage(e, "could not be uploaded")}`);
    }
  }
  return failures;
}

export function releaseShots(shots: PendingShot[]) {
  shots.forEach((s) => URL.revokeObjectURL(s.url));
}

// ── Picker ────────────────────────────────────────────────────────────────

const smallBtn: React.CSSProperties = { ...btnSecondary, padding: "6px 11px", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6 };

export function ScreenshotPicker({ shots, onChange, max = 6, onHideDialog, compact = false }: {
  shots: PendingShot[];
  onChange: (shots: PendingShot[]) => void;
  max?: number;
  /** Hides the surrounding dialog while the screen is captured. */
  onHideDialog?: (hidden: boolean) => void;
  compact?: boolean;
}) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<PendingShot | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const shotsRef = useRef(shots);
  shotsRef.current = shots;

  const add = useCallback(async (make: () => Promise<PendingShot | null>) => {
    if (shotsRef.current.length >= max) { setError(`Up to ${max} screenshots at a time.`); return; }
    setBusy(true);
    setError(null);
    try {
      const shot = await make();
      if (shot) onChange([...shotsRef.current, shot]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "That image could not be added.");
    } finally {
      setBusy(false);
    }
  }, [max, onChange]);

  // Paste an image anywhere while the picker is on screen.
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const item = Array.from(e.clipboardData?.items ?? []).find((i) => i.kind === "file" && i.type.startsWith("image/"));
      const file = item?.getAsFile();
      // Another picker on the page (the admin tab under the dialog) may have taken it already.
      if (!file || e.defaultPrevented) return;
      e.preventDefault();
      void add(() => fromFile(file, "pasted-screenshot"));
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, [add]);

  function remove(key: string) {
    const gone = shots.find((s) => s.key === key);
    if (gone) URL.revokeObjectURL(gone.url);
    onChange(shots.filter((s) => s.key !== key));
  }

  return (
    <div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        {canCaptureScreen && (
          <button type="button" style={smallBtn} disabled={busy} onClick={() => add(() => captureScreen(onHideDialog ?? (() => undefined)))}>
            <Camera size={14} /> Capture screen
          </button>
        )}
        <button type="button" style={smallBtn} disabled={busy} onClick={() => fileRef.current?.click()}>
          <ImagePlus size={14} /> Add image
        </button>
        <input
          ref={fileRef}
          type="file"
          accept={ACCEPTED.join(",")}
          multiple
          style={{ display: "none" }}
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            e.target.value = "";
            void (async () => { for (const f of files) await add(() => fromFile(f, f.name)); })();
          }}
        />
        {!compact && <span style={{ fontSize: 11.5, color: theme.color.subtle }}>or paste an image. Click a screenshot to black out anything private.</span>}
        {busy && <span style={{ fontSize: 11.5, color: theme.color.subtle }}>Preparing...</span>}
      </div>
      {error && <div role="alert" style={{ color: theme.color.dangerText, fontSize: 12, marginTop: 6 }}>{error}</div>}
      {shots.length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
          {shots.map((s) => (
            <div key={s.key} style={{ position: "relative", width: 112, border: `1px solid ${theme.color.border}`, borderRadius: 3, overflow: "hidden", background: theme.color.panelAlt }}>
              <button type="button" onClick={() => setEditing(s)} title="Mark up or black out" aria-label={`Edit ${s.name}`} style={{ display: "block", padding: 0, border: "none", background: "none", cursor: "pointer", width: "100%" }}>
                <img src={s.url} alt={s.name} style={{ display: "block", width: "100%", height: 72, objectFit: "cover" }} />
              </button>
              <div style={{ display: "flex", alignItems: "center", gap: 2, padding: "2px 4px" }}>
                <span style={{ flex: 1, fontSize: 10, color: theme.color.subtle, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</span>
                <button type="button" onClick={() => setEditing(s)} aria-label={`Edit ${s.name}`} style={iconBtn}><Pencil size={12} /></button>
                <button type="button" onClick={() => remove(s.key)} aria-label={`Remove ${s.name}`} style={iconBtn}><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && (
        <ScreenshotEditor
          shot={editing}
          onCancel={() => setEditing(null)}
          onSave={(next) => {
            URL.revokeObjectURL(editing.url);
            onChange(shots.map((s) => (s.key === editing.key ? next : s)));
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

const iconBtn: React.CSSProperties = { background: "none", border: "none", cursor: "pointer", color: "var(--c-muted)", padding: 2, display: "flex", borderRadius: 4 };

// ── Editor: black out or highlight ────────────────────────────────────────

type Mark = { kind: "redact" | "highlight"; x: number; y: number; w: number; h: number };

function ScreenshotEditor({ shot, onSave, onCancel }: { shot: PendingShot; onSave: (s: PendingShot) => void; onCancel: () => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [tool, setTool] = useState<Mark["kind"]>("redact");
  const [marks, setMarks] = useState<Mark[]>([]);
  const [draft, setDraft] = useState<Mark | null>(null);
  const [saving, setSaving] = useState(false);
  const start = useRef<{ x: number; y: number } | null>(null);
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  useEffect(() => {
    const img = new Image();
    img.onload = () => setImage(img);
    img.src = shot.url;
  }, [shot.url]);

  // Escape leaves the editor, not the dialog under it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      onCancelRef.current();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);

  const draw = useCallback((ctx: CanvasRenderingContext2D, all: Mark[]) => {
    if (!image) return;
    ctx.drawImage(image, 0, 0);
    for (const m of all) {
      if (m.kind === "redact") {
        ctx.fillStyle = "#000";
        ctx.fillRect(m.x, m.y, m.w, m.h);
      } else {
        ctx.strokeStyle = cbSafe("#E11D48");
        ctx.lineWidth = Math.max(3, Math.round(image.width / 400));
        ctx.strokeRect(m.x, m.y, m.w, m.h);
      }
    }
  }, [image]);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c || !image) return;
    c.width = image.width;
    c.height = image.height;
    const ctx = c.getContext("2d");
    if (ctx) draw(ctx, draft ? [...marks, draft] : marks);
  }, [image, marks, draft, draw]);

  function point(e: React.PointerEvent<HTMLCanvasElement>) {
    const c = canvasRef.current!;
    const r = c.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / r.width) * c.width, y: ((e.clientY - r.top) / r.height) * c.height };
  }
  function rect(a: { x: number; y: number }, b: { x: number; y: number }): Mark {
    return { kind: tool, x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), w: Math.abs(a.x - b.x), h: Math.abs(a.y - b.y) };
  }

  async function save() {
    const c = canvasRef.current;
    if (!c || !image) return;
    setSaving(true);
    try {
      const ctx = c.getContext("2d");
      if (ctx) draw(ctx, marks);
      onSave(await normalise(c, c.width, c.height, shot.name));
    } finally {
      setSaving(false);
    }
  }

  const toolBtn = (k: Mark["kind"], label: string) => (
    <button
      type="button"
      aria-pressed={tool === k}
      onClick={() => setTool(k)}
      style={{ ...smallBtn, ...(tool === k ? { borderColor: theme.color.accent, color: theme.color.accentText, background: theme.color.accentBg } : {}) }}
    >
      {label}
    </button>
  );

  return (
    <div role="dialog" aria-modal="true" aria-label="Mark up screenshot" style={{ position: "fixed", inset: 0, zIndex: 1100, background: "rgba(0,0,0,0.75)", display: "flex", flexDirection: "column", padding: 16 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", ...floatingSheet, padding: "10px 12px", marginBottom: 10 }}>
        {toolBtn("redact", "Black out")}
        {toolBtn("highlight", "Highlight")}
        <button type="button" style={smallBtn} disabled={marks.length === 0} onClick={() => setMarks(marks.slice(0, -1))}><Undo2 size={14} /> Undo</button>
        <span style={{ fontSize: 12, color: theme.color.subtle, flex: 1 }}>Drag over the screenshot. Blacked-out areas are removed from the image before it is sent.</span>
        <button type="button" style={smallBtn} onClick={onCancel}><X size={14} /> Cancel</button>
        <button type="button" style={{ ...btnPrimary, padding: "6px 14px", fontSize: 12 }} disabled={saving || !image} onClick={save}>{saving ? "Saving..." : "Done"}</button>
      </div>
      <div style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", overflow: "auto" }}>
        <canvas
          ref={canvasRef}
          style={{ maxWidth: "100%", maxHeight: "100%", cursor: "crosshair", touchAction: "none", background: "#fff" }}
          onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); start.current = point(e); }}
          onPointerMove={(e) => { if (start.current) setDraft(rect(start.current, point(e))); }}
          onPointerUp={(e) => {
            if (!start.current) return;
            const m = rect(start.current, point(e));
            start.current = null;
            setDraft(null);
            if (m.w > 3 && m.h > 3) setMarks((all) => [...all, m]);
          }}
        />
      </div>
    </div>
  );
}

// ── Gallery: screenshots already on a ticket ──────────────────────────────

/** Thumbnails fetched through the API (they need the session), opened full size on click. */
export function AttachmentGallery({ attachments, basePath }: { attachments: BugAttachment[]; basePath: string }) {
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [open, setOpen] = useState<BugAttachment | null>(null);
  // Callers pass a freshly filtered array each render; fetch again only when the set changes.
  const ids = attachments.map((a) => a.id).join(",");

  useEffect(() => {
    let cancelled = false;
    const made: string[] = [];
    (async () => {
      for (const id of ids ? ids.split(",") : []) {
        const a = { id };
        try {
          const blob = await apiBlob(`${basePath}/${encodeURIComponent(a.id)}`);
          if (cancelled) return;
          const url = URL.createObjectURL(blob);
          made.push(url);
          setUrls((u) => ({ ...u, [a.id]: url }));
        } catch { /* shown as unavailable */ }
      }
    })();
    return () => { cancelled = true; made.forEach((u) => URL.revokeObjectURL(u)); setUrls({}); };
  }, [ids, basePath]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); setOpen(null); } };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open]);

  if (attachments.length === 0) return null;
  return (
    <>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
        {attachments.map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() => urls[a.id] && setOpen(a)}
            title={a.fileName}
            aria-label={`Open ${a.fileName}`}
            style={{ padding: 0, width: 104, height: 70, border: `1px solid ${theme.color.border}`, borderRadius: 3, overflow: "hidden", cursor: urls[a.id] ? "zoom-in" : "default", background: theme.color.panelAlt, fontSize: 10, color: theme.color.subtle }}
          >
            {urls[a.id] ? <img src={urls[a.id]} alt={a.fileName} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} /> : "Loading..."}
          </button>
        ))}
      </div>
      {open && urls[open.id] && (
        <div role="dialog" aria-modal="true" aria-label={open.fileName} onClick={() => setOpen(null)} style={{ position: "fixed", inset: 0, zIndex: 1100, background: "rgba(0,0,0,0.85)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 16, cursor: "zoom-out" }}>
          <img src={urls[open.id]} alt={open.fileName} style={{ maxWidth: "100%", maxHeight: "calc(100% - 40px)", boxShadow: "var(--c-sheetShadow)" }} />
          <div style={{ color: "#fff", fontSize: 12, marginTop: 10 }}>
            {open.fileName}{open.width && open.height ? ` · ${open.width}x${open.height}` : ""} ·{" "}
            <a href={urls[open.id]} download={open.fileName} onClick={(e) => e.stopPropagation()} style={{ color: "#fff" }}>Download</a>
          </div>
        </div>
      )}
    </>
  );
}
